import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from 'state/axios';
import { format, addDays, compareAsc } from "date-fns";
import toast from 'react-hot-toast';
import { useMediaQuery, useTheme, Box, Typography, 
    CircularProgress, Tooltip, 
    FormControl, MenuItem, InputLabel, Select,  
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT, 
    OTP_DELAY_MINUTES, 
    DIVERT_RETURN_STATUS,
    EXCLUDED_FLIGHT_STATUS,
} from 'state/constants';
import { fetchLegMembersByFlt } from 'state/api';
import { removeItemByValueImmutable, 
    filterFlights,
} from 'state/helpers';
import { hasPermission } from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import PerformanceGauge from 'components/PerformanceGauge';
import DataGridComponent from 'components/DataGridComponent';
import FlightFilterDialog from 'components/FlightFilterDialog';
import CrewListDialog from 'components/CrewListDialog';
import FlightSlotList from "./FlightSlotList";


const FlightSlots = () => {
    useAuthCheck();
    const user = useSelector((state) => state.user); 
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const primaryDark = theme.palette.primary.dark;
    const textPrimaryColor = theme.palette.text.primary;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const canCopy = hasPermission(user?.permissions, "FlightSlots", "copy", false);
      
    const { startDate, endDate } = useDateRangeContext();
    
    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
    const [sortOption, setSortOption] = useState('none'); // none, flightReg_fullEtd, fullEtd_flightReg
    const [port, setPort] = useState('ALL');

    const [flight, setFlight] = useState([]);
    const [crewList, setCrewList] = useState([]);
    const [jumpList, setJumpList] = useState([]);

    const [flights, setFlights] = useState([]);
    

    useEffect(() => {        
        const handleFlights = async () => {
            if (!startDate || !endDate) return null; 

            try {           
                setLoading(true);
                const url = '/flights/flightslots';        
                const payload = { startDate: addDays(startDate, -1), endDate: addDays(endDate, 1) };        
                
                const response = await api.post(url, payload);
                
                if (response.data) {        
                    const filteredFlights = response.data.filter(flight => {
                        const flightDateToCompare =
                            timeMode === 'UTC' ? flight.flightDate : flight.flightDateLocalBase;
                        
                        return (
                            compareAsc(flightDateToCompare, startDate) >= 0 &&
                            compareAsc(flightDateToCompare, endDate) <= 0
                        );
                    });

                    const processedFlights = removeItemByValueImmutable(
                        filteredFlights, 
                        'flightStatus', 
                        EXCLUDED_FLIGHT_STATUS
                    );
                    
                    const nonDivFlights = processedFlights.filter(flight => 
                        !DIVERT_RETURN_STATUS.includes(flight.flightStatus)
                    );

                    setFlights(nonDivFlights);
                }                
            } catch (error) {
                setError(error);
                toast.error(error?.message || "Failed to fetch flights!");
            } finally {
                setLoading(false);
            }
        };

        handleFlights();        
    }, [timeMode, startDate, endDate]);
             
    const filteredFlights = useMemo(() => {
        return filterFlights({ flights, filters, timeMode });
    }, [flights, filters, timeMode]);       
    
    const sortedFlights = useMemo(() => {
        if (sortOption === 'none') return filteredFlights;

        const sorted = [...filteredFlights];
        sorted.sort((a, b) => {
            const aEtd = a.fullEtd ? new Date(a.fullEtd) : null;
            const bEtd = b.fullEtd ? new Date(b.fullEtd) : null;
            const aReg = a.flightReg || '';
            const bReg = b.flightReg || '';

            // sortOption === 'flightReg_fullEtd')                
            if (aReg !== bReg) return aReg.localeCompare(bReg);
            if (!aEtd && !bEtd) return 0;
            if (!aEtd) return 1;
            if (!bEtd) return -1;
            return aEtd - bEtd;
        });

        return sorted;
    }, [filteredFlights, sortOption]);

    const departures = useMemo(() => {
        if (port === 'ALL') return sortedFlights;
        return sortedFlights?.filter(flight => flight.dep === port);
    }, [sortedFlights, port]);
   
    // Calculate metrics for gauges    
    const cancelledRatio = useMemo(() => {
        if (!departures.length) return 0;
        const filtered = departures?.filter(flight => flight.state === 'Cancelled').length;
        return ((filtered / departures.length) * 100).toFixed(1);
    }, [departures]);

    const newRatio = useMemo(() => {
        if (!departures.length) return 0;
        const filtered = departures?.filter(flight => flight.state === 'New').length;
        return ((filtered / departures.length) * 100).toFixed(1);
    }, [departures]);

    const accuracyRatio = useMemo(() => {
        if (!departures.length) return 0;
        const filtered = departures?.filter(flight => 
            flight.state !== 'New'
            && flight.state !== 'Cancelled'
            && (flight.sectorType === 'Domestic' 
                ? flight.tdDiff > 15 || flight.tdDiff < -15
                : flight.tdDiff > 30 || flight.tdDiff < -30
            )
        ).length;
        return ((1 - filtered / departures.length) * 100).toFixed(1);
    }, [departures]);

    const punctualityDep = useMemo(() => {
        if (!departures.length) return 100;
        const onTimeDepartures = departures?.filter(flight => flight.state !== 'Cancelled' && (flight.depDelaysTime || 0) <= OTP_DELAY_MINUTES).length;
        return ((onTimeDepartures / departures.length) * 100).toFixed(1);
    }, [departures]);

    const punctualityArr = useMemo(() => {
        if (!departures.length) return 100;
        const onTimeArrivals = departures?.filter(flight => flight.state !== 'Cancelled' && (flight.arrDelaysTime || 0) <= OTP_DELAY_MINUTES).length;
        return ((onTimeArrivals / departures.length) * 100).toFixed(1);
    }, [departures]);
        

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const handleSortToggle = () => {
        setSortOption((prev) => {
            if (prev === 'none') return 'flightReg_fullEtd';
            if (prev === 'flightReg_fullEtd') return 'none';
            return 'none';
        });
    };

    const handleCopy = async () => {
        if (!startDate || !endDate) {
            toast.error("Please provide a valid date range.");
            return;
        }
    
        try {
            const url = '/flights/copytobaseflights';
            const payload = { startDate, endDate };          
    
            const res = await api.post(url, payload);
            if (res.data?.success) {
                setFlights(res.data.data || []);
                toast.success(res.data.message || "Flights copied successfully!");
            } else {
                toast.error(res.data?.message || "Failed to copy flights.");
            }
        } catch (error) {
            // Handle Axios-specific errors
            if (error.response) {
                const { status, data } = error.response;
                if (status === 400) {
                    toast.error(data.message || "Invalid date range provided.");
                } else if (status === 404) {
                    toast.error(data.message || "No flights found to copy.");
                } else {
                    toast.error(data.message || "Failed to copy flights.");
                }
            } else {
                toast.error(error.message || "Network error. Please try again.");
            }
        }
    };

    const handleViewClick = useCallback(async (param) => {
        try {
            const {flightDetails, legMembers, jumpSeaters} = await fetchLegMembersByFlt(param.flightDate, param.flightDesc, param.dep);
            
            setFlight(flightDetails);
            setCrewList(legMembers);
            setJumpList(jumpSeaters);
            setOpenCrewListDialog(true);
        } catch (error) {
            setOpenCrewListDialog(false);
        } 
    }, []);
    
    const handleCloseCrewListDialog = () => {
        setOpenCrewListDialog(false);
        setFlight([]);
        setCrewList([]);
        setJumpList([]);
    }; 

    const options = useMemo(() => {
        const acTypes = new Set();
        const flightRegs = new Set();
        const sectorTypes = new Set();
        const departureTypes = new Set();
        const arrivalTypes = new Set();

        if (Array.isArray(flights)) {
            flights.forEach(f => {
                if (f.acType) acTypes.add(f.acType);
                if (f.flightReg) flightRegs.add(f.flightReg);
                if (f.sectorType) sectorTypes.add(f.sectorType);
                if (f.departureType) departureTypes.add(f.departureType);
                if (f.arrivalType) arrivalTypes.add(f.arrivalType);
            });
        }

        return {
            acTypeOptions: Array.from(acTypes).sort(),
            flightRegOptions: Array.from(flightRegs).sort(),
            sectorTypeOptions: Array.from(sectorTypes).sort(),
            departureTypeOptions: Array.from(departureTypes).sort(),
            arrivalTypeOptions: Array.from(arrivalTypes).sort(),
        };
    }, [flights]); 

    const airportOptions = useMemo(() => {
        if (!Array.isArray(filteredFlights)) return [];
      
        const depPorts = filteredFlights.map(flight => flight.dep);
        const arrPorts = filteredFlights.map(flight => flight.arr);
        const combinedPorts = [...depPorts, ...arrPorts];
        
        return Array.from(new Set(combinedPorts)).sort(); // Unique & sorted
    }, [filteredFlights]);

    // Define delay thresholds and styles
    const DELAY_THRESHOLDS = useMemo(() => ({
        HIGH: { minutes: 60, color: '#f2292d' },
        MEDIUM: { minutes: 30, color: '#f29129' },
        LOW: { minutes: 15, color: '#d7f229' },
    }), []);
      
    // Centralized animation styles
    const BLINK_ANIMATION = useMemo(() => ({
        animation: 'blink 1s step-start 0s infinite',
        fontWeight: 'bold',
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
        },
    }), []);
      
    const getBlinkStyles = useCallback((delay) => {
        if (delay > DELAY_THRESHOLDS.HIGH.minutes) {
            return {
                ...BLINK_ANIMATION,
                color: DELAY_THRESHOLDS.HIGH.color,
            };
        }
        if (delay > DELAY_THRESHOLDS.MEDIUM.minutes) {
            return {
                ...BLINK_ANIMATION,
                color: DELAY_THRESHOLDS.MEDIUM.color,
            };
        }
        if (delay > DELAY_THRESHOLDS.LOW.minutes) {
            return {
                ...BLINK_ANIMATION,
                color: DELAY_THRESHOLDS.LOW.color,
            };
        }
        return {};
    }, [BLINK_ANIMATION, DELAY_THRESHOLDS]);
    
    const createTimeColumn = useCallback(({ field, headerName, delayField, timeMode, isMobile }) => (
        {
            field: timeMode === 'UTC' ? field : `${field}LocalBase`,
            headerName,
            headerClassName: 'theme-column--header',
            renderCell: ({ row }) => (
                <Box
                    height="100%"
                    width="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        '@keyframes blink': {
                            '60%': { opacity: 0.6 },
                        },
                    }}
                >
                    <Typography variant="body2" sx={getBlinkStyles(row[delayField] || 0)}>
                        {row[timeMode === 'UTC' ? field : `${field}LocalBase`] || ''}
                    </Typography>
                </Box>
            ),
            ...(isMobile ? { width: 80 } : { flex: 1.2 }),
        }), [getBlinkStyles]);           

    const columns = useMemo(() => [
        {
            field: "no",
            headerName: "No",
            headerClassName: 'theme-column--header',
            filterable: false,      
            sortable: false,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
            ...(() => {
                return isMobile ? { width: 60 } : { flex: 1 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase",
            headerName: "FlightDate",
            headerClassName: 'theme-column--header',
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "dd/MM/yy")}`;
            },
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.5 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "flightActualDayDep" : "flightActualDayDepLocalBase",
            headerName: "Date",
            headerClassName: 'theme-column--header',
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "dd/MM/yy")}`;
            },
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.5 };
            })(),
        },
        // {
        //     field: timeMode === 'UTC' ? "flightActualDayArr" : "flightActualDayArrLocalBase",
        //     headerName: "ActualDayArr",
        //     ...(() => {
        //         return isMobile ? { width: 100 } : { flex: 1.5 };
        //     })(),
        // },        
        {
            field: "flightDesc",
            headerName: "FlightDesc",
            headerClassName: 'theme-column--header',
            cellClassName: "name-column--cell",
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "flightNo",
            headerName: "FlightNo",
            headerClassName: 'theme-column--header',
            cellClassName: "name-column--cell",
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "sector",
            headerName: "Sector",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.8 };
            })(),
        },
        {
            field: "acType",
            headerName: "ACType",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "flightReg",
            headerName: "Reg",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.5 };
            })(),
        },
        {
            field: "flightType",
            headerName: "FlightType",
            ...(() => {
                return isMobile ? { width: 140 } : { flex: 2 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "std" : "stdLocalBase",
            headerName: "Std",
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "sta" : "staLocalBase",
            headerName: "Sta",
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        createTimeColumn({
            field: 'etd',
            headerName: 'Etd',
            delayField: 'tdDiff',
            timeMode,
            isMobile,
        }),
        createTimeColumn({
            field: 'eta',
            headerName: 'Eta',
            delayField: 'taDiff',
            timeMode,
            isMobile,
        }),  
        {
            field: timeMode === 'UTC' ? "atd" : "atdLocalBase",
            headerName: "Atd",
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },   
        {
            field: timeMode === 'UTC' ? "takeOff" : "takeOffLocalBase",
            headerName: "TakeOff",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "touchDown" : "touchDownLocalBase",
            headerName: "TouchDown",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },          
        {
            field: timeMode === 'UTC' ? "ata" : "ataLocalBase",
            headerName: "Ata",
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "tatDiff",
            headerName: "TATDiff",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "taxiOut",
            headerName: "TaxiOut",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "taxiIn",
            headerName: "TaxiIn",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "airMinutes",
            headerName: "AirMinutes",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        // {
        //     field: "blockHours",
        //     headerName: "BlockHour",
        //     headerClassName: 'theme-column--header',
        //     valueFormatter: (params) => {
        //         const value = params || 0; 
        //         return parseFloat(value).toFixed(2); 
        //       },
        // },
        {
            field: "blockMinutes",
            headerName: "BlockMinutes",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "flightDepDelays",
            headerName: "DepDelays",
            ...(() => {
                return isMobile ? { width: 130 } : { flex: 2 };
            })(),
        },
        {
            field: "depDelaysTime",
            headerName: "DepDelaysTime",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "flightArrDelays",
            headerName: "ArrDelays",
            ...(() => {
                return isMobile ? { width: 130 } : { flex: 2 };
            })(),
        },
        {
            field: "arrDelaysTime",
            headerName: "ArrDelaysTime",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "sectorType",
            headerName: "SectorType",
            ...(() => {
                return isMobile ? { width: 130 } : { flex: 2 };
            })(),
        },        
        {
            field: "flightStatus",
            headerName: "Status",
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.5 };
            })(),
        },
        {
            field: "noOfPax",
            headerName: "Pax",
            ...(() => {
                return isMobile ? { width: 60 } : { flex: 1 };
            })(),
        },
        {
            field: "loadFactor",
            headerName: "LF",
            valueFormatter: (params) => {
                return params !== undefined && params !== null
                    ? params.toFixed(2) 
                    : "0";          
            },
            ...(() => {
                return isMobile ? { width: 60 } : { flex: 1 };
            })(),
        },  
        {
            field: "tdDiff",
            headerName: "TdDiff",
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "taDiff",
            headerName: "TaDiff",
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "state",
            headerName: "State",
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
    ], [timeMode, isMobile, createTimeColumn]);
    
    const hiddenColumns = useMemo(() => {
        return {
            _id: false,
            no: false,
            flightDate: false,
            flightDateLocalBase: false,
            flightDesc: false,
            acType: false,
            flightReg: false,
            flightType: false,        
            std: false,
            stdLocalBase: false,
            sta: false,
            staLocalBase: false,
            atd: false,
            atdLocalBase: false,
            ata: false,
            ataLocalBase: false,
            takeOff: false,
            takeOffLocalBase: false,
            touchDown: false,
            touchDownLocalBase: false,
            taxiOut: false,
            taxiIn: false,
            tatDiff: false,
            airMinutes: false,
            blockMinutes: false,
            flightDepDelays: false,
            depDelaysTime: false,
            flightArrDelays: false,
            arrDelaysTime: false,
            sectorType: false,
            flightStatus: false,
            noOfPax: false,
            loadFactor: false,
        }
    }, []);

    const nonMobileHiddenColumns = useMemo(() => {
        return {
            _id: false,
            flightDate: false,
            flightDateLocalBase: false,
            flightDesc: false,
            flightType: false,
            std: false,
            stdLocalBase: false,
            sta: false,
            staLocalBase: false,
            takeOff: false,
            takeOffLocalBase: false,
            touchDown: false,
            touchDownLocalBase: false,
            taxiOut: false,
            taxiIn: false,
            tatDiff: false,
            airMinutes: false,
            blockMinutes: false,
            flightDepDelays: false,
            depDelaysTime: false,
            flightArrDelays: false,
            arrDelaysTime: false,
            sectorType: false,
            flightStatus: false,
            noOfPax: false,
            loadFactor: false,
        }
    }, []);
        
    const columnsToShow = [...columns];
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(
        isMobile ? hiddenColumns : nonMobileHiddenColumns
    );

    useEffect(() => {
        setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
    }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);
       
    
    return (
        <Box className="main-container-box">                 
            <FlexBetween>
                <Header 
                    title="Flight Slots" 
                    subtitle={loading ? 'Loading...' : `List of ${departures?.length || 0} Slots`}
                />
                {!loading && (                    
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl
                            size="small"
                            sx={{ width: isMobile ? '5rem' : '6rem' }}
                        >
                            <InputLabel>Port</InputLabel>
                            <Select
                                label="Port"
                                value={port ? port : ''}
                                onChange={(e) => setPort(e.target.value)}                                
                            >
                                <MenuItem key='ALL' value='ALL'>ALL</MenuItem>
                                {airportOptions?.map((airport, index) => (
                                    <MenuItem key={index} value={airport}>
                                        {airport}
                                    </MenuItem>
                                ))}                                                               
                            </Select>
                        </FormControl>
                        {canCopy && (
                            <Tooltip title='Copy flights to base flights' arrow>
                                <span>
                                    <StyledButton 
                                        onClick={() => {
                                            if (!canCopy) return;
                                            handleCopy();
                                        }}
                                    >
                                        <CopyAllIcon />
                                    </StyledButton>
                                </span>
                            </Tooltip>      
                        )} 
                        <Tooltip title={`Sort by ${sortOption === 'flightReg_fullEtd' ? 'Reg + Etd' : 'Etd'}`} arrow>
                            <span>
                                <StyledButton 
                                    onClick={handleSortToggle}
                                    sx={{
                                        color: sortOption === 'none' 
                                            ? `${textPrimaryColor} !important` 
                                            : `${primaryDark} !important`,
                                    }}
                                >
                                    <SortIcon />
                                </StyledButton>
                            </span>
                        </Tooltip>
                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                
            </FlexBetween>          
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                    sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
                >
                    <Box
                        ref={scrollRef}
                        height={LAYOUT_HEIGHT}  
                        display={"flex"}
                        flexDirection={"column"}
                        gap={1}
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
                    >        
                        <Box                             
                            display="flex" 
                            alignItems="center" 
                            justifyContent={isMobile ? "center" : "flex-end"} 
                            height={68}
                            gap={isMobile ? 1 : 2}
                        >                                
                            <PerformanceGauge
                                title="Dep OTP"
                                indicator={punctualityDep}
                                pathColor="#6bf2c1"
                            />
                            <PerformanceGauge
                                title="Arr OTP"
                                indicator={punctualityArr}
                                pathColor="#63b9f2"
                            />
                            <PerformanceGauge
                                title="Cancelled"
                                indicator={cancelledRatio}
                                pathColor="#ed1515"
                            />
                            <PerformanceGauge
                                title="New"
                                indicator={newRatio}
                                pathColor="#cded15"
                            />
                            <PerformanceGauge
                                title="Accuracy"
                                indicator={accuracyRatio}
                                pathColor="#0ceef2"
                            />
                        </Box>     

                        {isMobile ? (
                            <FlightSlotList 
                                flights={departures}                                
                                onClick={handleViewClick}
                            />
                        ) : (
                            <Box height={`calc(${LAYOUT_HEIGHT} - 78px)`}>
                                <DataGridComponent
                                    rows={departures}
                                    columns={columnsToShow}
                                    columnVisibilityModel={columnVisibilityModel}
                                    setColumnVisibilityModel={setColumnVisibilityModel}
                                    loading={loading}
                                />
                            </Box>
                        )}
                        
                        <ScrollToTop scrollRef={scrollRef} />                                                                
                    </Box>
                </Box>
            )}
            
            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={options.acTypeOptions} 
                    flightRegOptions={options.flightRegOptions}
                    sectorTypeOptions={options.sectorTypeOptions}
                    departureTypeOptions={options.departureTypeOptions}
                    arrivalTypeOptions={options.arrivalTypeOptions}
                    currentFilters={filters}
                />
            }
            
            {openCrewListDialog &&
                <CrewListDialog 
                    open={openCrewListDialog}
                    onClose={handleCloseCrewListDialog}
                    flight={flight}
                    crewList={crewList}
                    jumpList={jumpList}
                />
            }            
        </Box>
    );
};

export default FlightSlots;
